/**
 * Activity Log Page
 *
 * View audit trail of user actions across the system.
 */

import { useState, useMemo } from 'react';
import {
  Typography,
  Table,
  Card,
  Space,
  Tag,
  Select,
  DatePicker,
  Row,
  Col,
  Statistic,
  Spin,
  Empty,
  Tooltip,
  Avatar,
} from 'antd';
import {
  HistoryOutlined,
  UserOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  LoginOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import {
  activityLogsApi,
  type ActivityLog,
  ACTION_LABELS,
  ENTITY_LABELS,
} from '@api/activity-logs';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// Action icons
const ACTION_ICONS: Record<string, React.ReactNode> = {
  create: <PlusOutlined style={{ color: 'var(--status-success)' }} />,
  update: <EditOutlined style={{ color: 'var(--status-info)' }} />,
  delete: <DeleteOutlined style={{ color: 'var(--status-error)' }} />,
  view: <EyeOutlined style={{ color: 'var(--text-tertiary)' }} />,
  complete: <CheckCircleOutlined style={{ color: 'var(--status-success)' }} />,
  login: <LoginOutlined style={{ color: '#722ed1' }} />,
};

// Action tag colors
const ACTION_COLORS: Record<string, string> = {
  create: 'green',
  update: 'blue',
  delete: 'red',
  view: 'default',
  complete: 'green',
  login: 'purple',
  logout: 'orange',
  archive: 'orange',
  restore: 'cyan',
};

export default function ActivityLogPage() {
  // Filters
  const [page, setPage] = useState(1);
  const [entityType, setEntityType] = useState<string | undefined>();
  const [action, setAction] = useState<string | undefined>();
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);

  // Build params
  const params = useMemo(() => ({
    page,
    limit: 25,
    entity_type: entityType,
    action,
    start_date: dateRange?.[0]?.toISOString(),
    end_date: dateRange?.[1]?.toISOString(),
  }), [page, entityType, action, dateRange]);

  // Fetch logs
  const { data: logsData, isLoading: logsLoading } = useQuery({
    queryKey: ['activity-logs', params],
    queryFn: () => activityLogsApi.list(params),
  });

  // Fetch stats
  const { data: statsData, isLoading: statsLoading } = useQuery({
    queryKey: ['activity-logs-stats'],
    queryFn: () => activityLogsApi.getStats(7),
  });

  const logs = logsData?.items || [];
  const total = logsData?.total || 0;

  // Parse changes JSON
  const parseChanges = (changes?: string): Record<string, { old: unknown; new: unknown }> | null => {
    if (!changes) return null;
    try {
      return JSON.parse(changes);
    } catch {
      return null;
    }
  };

  // Table columns
  const columns: ColumnsType<ActivityLog> = [
    {
      title: 'Time',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 140,
      render: (date: string) => (
        <Tooltip title={dayjs(date).format('YYYY-MM-DD HH:mm:ss')}>
          <Text type="secondary">{dayjs(date).fromNow()}</Text>
        </Tooltip>
      ),
    },
    {
      title: 'User',
      key: 'user',
      width: 180,
      render: (_: unknown, record: ActivityLog) => (
        <Space size="small">
          <Avatar size="small" icon={<UserOutlined />} />
          <div>
            <div><Text strong>{record.user_name || 'System'}</Text></div>
            {record.user_email && (
              <Text type="secondary" style={{ fontSize: 11 }}>{record.user_email}</Text>
            )}
          </div>
        </Space>
      ),
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      width: 100,
      render: (action: string) => (
        <Tag
          icon={ACTION_ICONS[action]}
          color={ACTION_COLORS[action] || 'default'}
        >
          {ACTION_LABELS[action] || action}
        </Tag>
      ),
    },
    {
      title: 'Entity',
      key: 'entity',
      render: (_: unknown, record: ActivityLog) => (
        <div>
          <Tag>{ENTITY_LABELS[record.entity_type] || record.entity_type}</Tag>
          {record.entity_name && (
            <Text style={{ marginLeft: 8 }}>{record.entity_name}</Text>
          )}
        </div>
      ),
    },
    {
      title: 'Changes',
      dataIndex: 'changes',
      key: 'changes',
      ellipsis: true,
      render: (changes: string) => {
        const parsed = parseChanges(changes);
        if (!parsed) return '-';

        const fields = Object.keys(parsed);
        if (fields.length === 0) return '-';

        return (
          <Tooltip
            title={
              <div>
                {fields.map((field) => (
                  <div key={field}>
                    <strong>{field}:</strong>{' '}
                    <Text delete type="secondary">
                      {String(parsed[field].old || '(empty)')}
                    </Text>{' '}
                    {'→'}{' '}
                    <Text>{String(parsed[field].new || '(empty)')}</Text>
                  </div>
                ))}
              </div>
            }
          >
            <Text type="secondary">
              {fields.length} field{fields.length !== 1 ? 's' : ''} changed
            </Text>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={4} style={{ margin: 0 }}>
          <HistoryOutlined style={{ marginRight: 8 }} />
          Activity Log
        </Title>
        <Text type="secondary">Track user actions and system changes</Text>
      </div>

      {/* Stats Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="Last 7 Days"
              value={statsData?.total || 0}
              loading={statsLoading}
              suffix="actions"
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="Most Common"
              value={statsData?.by_action?.[0]?.action || '-'}
              loading={statsLoading}
              valueStyle={{ fontSize: 18 }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="Top Entity"
              value={ENTITY_LABELS[statsData?.by_entity?.[0]?.entity_type || ''] || statsData?.by_entity?.[0]?.entity_type || '-'}
              loading={statsLoading}
              valueStyle={{ fontSize: 18 }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="Active Users"
              value={statsData?.by_user?.length || 0}
              loading={statsLoading}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          <Select
            placeholder="Entity type"
            allowClear
            style={{ width: 150 }}
            value={entityType}
            onChange={setEntityType}
            options={[
              { value: 'template', label: 'Templates' },
              { value: 'instance', label: 'Tasks' },
              { value: 'category', label: 'Categories' },
              { value: 'tag', label: 'Tags' },
              { value: 'user', label: 'Users' },
              { value: 'project', label: 'Projects' },
              { value: 'note', label: 'Notes' },
            ]}
          />
          <Select
            placeholder="Action"
            allowClear
            style={{ width: 120 }}
            value={action}
            onChange={setAction}
            options={[
              { value: 'create', label: 'Create' },
              { value: 'update', label: 'Update' },
              { value: 'delete', label: 'Delete' },
              { value: 'view', label: 'View' },
              { value: 'complete', label: 'Complete' },
              { value: 'login', label: 'Login' },
            ]}
          />
          <RangePicker
            value={dateRange}
            onChange={(dates) => setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs] | null)}
          />
        </Space>
      </Card>

      {/* Activity Table */}
      <Card>
        {logsLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
        ) : logs.length === 0 ? (
          <Empty description="No activity logs found" />
        ) : (
          <Table
            columns={columns}
            dataSource={logs}
            rowKey="id"
            size="small"
            pagination={{
              current: page,
              total,
              pageSize: 25,
              showSizeChanger: false,
              showTotal: (total) => `${total} activities`,
              onChange: setPage,
            }}
          />
        )}
      </Card>
    </div>
  );
}
